import { View, Text, FlatList, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '@/hooks/useAuth';
import { useConversations } from '@/hooks/useConversations';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { ChatDesktop } from '@/components/ChatDesktop';
import { ConversationWithStudent } from '@/lib/types';

function formatRelativeTime(iso: string | null): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export default function PersonalChatListScreen() {
  const { profile } = useAuth();
  const router = useRouter();
  const isDesktop = useIsDesktop();
  const { conversations, isLoading, error } = useConversations(profile?.id ?? '');

  // Em desktop: layout master-detail (lista + conversa ativa em 2 colunas)
  if (isDesktop) return <ChatDesktop />;

  if (isLoading) return <ActivityIndicator style={{ flex: 1 }} color="#C8102E" />;

  if (error) {
    return (
      <View style={styles.centered}>
        <Text style={styles.errorText}>Erro ao carregar conversas.</Text>
      </View>
    );
  }

  function renderItem({ item }: { item: ConversationWithStudent }) {
    const hasUnread = item.unread_personal > 0;
    const studentName = item.student?.full_name ?? 'Aluno';
    return (
      <TouchableOpacity
        style={styles.item}
        onPress={() => router.push(`/(personal)/chat/${item.id}` as any)}
        activeOpacity={0.7}
      >
        <View style={styles.avatar}>
          <Text style={styles.avatarLetter}>
            {studentName.charAt(0).toUpperCase()}
          </Text>
        </View>
        <View style={styles.itemContent}>
          <Text style={[styles.itemName, hasUnread && styles.itemNameBold]}>
            {studentName}
          </Text>
          {item.last_message_at && (
            <Text style={styles.itemTime}>{formatRelativeTime(item.last_message_at)}</Text>
          )}
        </View>
        {hasUnread && (
          <View style={styles.unreadBadge}>
            <Text style={styles.unreadBadgeText}>
              {item.unread_personal > 9 ? '9+' : String(item.unread_personal)}
            </Text>
          </View>
        )}
        <Ionicons name="chevron-forward" size={16} color="#ccc" />
      </TouchableOpacity>
    );
  }

  return (
    <FlatList
      style={styles.container}
      data={conversations}
      keyExtractor={(item) => item.id}
      renderItem={renderItem}
      ListEmptyComponent={
        <View style={styles.centered}>
          <Text style={styles.emptyText}>Nenhuma conversa ainda.</Text>
          <Text style={styles.emptySubText}>
            Quando um aluno abrir o chat, a conversa aparecerá aqui.
          </Text>
        </View>
      }
    />
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  centered: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingTop: 80, paddingHorizontal: 32 },
  errorText: { color: '#C8102E', fontSize: 14 },
  emptyText: { fontSize: 16, fontWeight: '600', color: '#888', textAlign: 'center' },
  emptySubText: { fontSize: 13, color: '#aaa', textAlign: 'center', marginTop: 8 },
  item: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: '#fff', paddingHorizontal: 16, paddingVertical: 14,
    borderBottomWidth: 1, borderBottomColor: '#f0f0f0',
  },
  avatar: {
    width: 44, height: 44, borderRadius: 22,
    backgroundColor: '#C8102E22', alignItems: 'center', justifyContent: 'center',
  },
  avatarLetter: { fontSize: 18, fontWeight: '700', color: '#C8102E' },
  itemContent: { flex: 1 },
  itemName: { fontSize: 15, color: '#1A1A1A' },
  itemNameBold: { fontWeight: '700' },
  itemTime: { fontSize: 12, color: '#888', marginTop: 2 },
  unreadBadge: {
    backgroundColor: '#C8102E', borderRadius: 10,
    minWidth: 20, height: 20,
    alignItems: 'center', justifyContent: 'center', paddingHorizontal: 5, marginRight: 4,
  },
  unreadBadgeText: { color: '#fff', fontSize: 11, fontWeight: '700' },
});
